import { useState, useEffect, useCallback } from 'react';
import { api } from '@/shared/lib/api';
import { captureError } from '@/shared/lib/monitoring';

export interface EditExample {
  id: number;
  sentence_en: string;
  sentence_en_blanked: string;
  sentence_ru: string;
  sentence_ru_blanked: string;
  difficulty: number;
}

export interface EditWordData {
  id: number;
  original_text: string;
  main_translation_text: string;
  translations: Array<{ id: number; translation_text: string }>;
  examples: EditExample[];
  // Маркер «это редактирование UserWord, а не DictionaryWord»:
  is_user_word?: boolean;
  dictionary_word_id?: number | null;
}

interface InlineWordEditProps {
  // Один из двух обязателен. dictionaryWordId приоритетнее.
  dictionaryWordId: number | null;
  userWordId?: number | null;
  onClose: () => void;
  // Колбэк после успешного сохранения слова (не примера).
  // Получает обновлённый word. Родитель решает, обновлять ли текущую карточку.
  onSaved?: (updatedWord: EditWordData) => void;
}

export function InlineWordEdit({ dictionaryWordId, userWordId, onClose, onSaved }: InlineWordEditProps) {
  // Режим определяется по наличию dictionary_word_id. Пользовательское слово
  // (только userWordId) грузится из /study/admin/user-words/:id, у него
  // нет blanked-версий примеров и нет автосинхронизации с другими юзерами.
  const isUserWord = dictionaryWordId == null && userWordId != null;
  const baseLoadUrl = isUserWord
    ? `/study/admin/user-words/${userWordId}`
    : `/study/admin/words/${dictionaryWordId}`;
  const baseSaveUrl = baseLoadUrl;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [wordData, setWordData] = useState<EditWordData | null>(null);
  const [mainTranslation, setMainTranslation] = useState('');
  const [altTranslations, setAltTranslations] = useState<string[]>([]);
  const [newAlt, setNewAlt] = useState('');
  const [examples, setExamples] = useState<EditExample[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [promoting, setPromoting] = useState(false);

  const loadWord = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get<EditWordData>(baseLoadUrl);
      setWordData(data);
      setMainTranslation(data.main_translation_text || '');
      setAltTranslations(data.translations?.map((t) => t.translation_text) ?? []);
      setExamples(data.examples ?? []);
    } catch (err) {
      captureError(err, { context: 'inline_word_edit_load' });
      setError('Не удалось загрузить слово');
    } finally {
      setLoading(false);
    }
  }, [baseLoadUrl]);

  useEffect(() => {
    loadWord();
  }, [loadWord]);

  const handleSave = async () => {
    if (!wordData) return;
    try {
      setSaving(true);
      const updated = await api.put<EditWordData>(baseSaveUrl, {
        main_translation_text: mainTranslation.trim(),
        alt_translations: altTranslations.filter(Boolean),
      });
      setSaved(true);
      if (onSaved && updated) onSaved(updated);
    } catch (err) {
      captureError(err, { context: 'inline_word_edit_save' });
      setError('Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  // Публикация пользовательского слова в общий словарь.
  // На дубликате (409) спрашивает подтверждение и повторяет с force=true.
  const handlePromote = async () => {
    if (!isUserWord || !userWordId || !wordData) return;
    if (!window.confirm(`Опубликовать «${wordData.original_text}» в общий словарь? Слово станет доступно всем пользователям.`)) return;
    setPromoting(true);
    setError(null);
    try {
      const tryPromote = async (force: boolean): Promise<void> => {
        try {
          const r = await api.post<{ ok: boolean; status?: 'created' | 'linked'; dictionary_word_id?: number }>(
            `/study/admin/user-words/${userWordId}/promote`,
            { force },
          );
          if (r?.ok) {
            const msg = r.status === 'linked'
              ? 'Слово привязано к существующему в общем словаре'
              : 'Слово опубликовано в общий словарь';
            window.alert('✅ ' + msg);
            // Сообщаем родителю что-то изменилось — он перезагрузит карточку
            if (onSaved && wordData) onSaved({ ...wordData, dictionary_word_id: r.dictionary_word_id });
            onClose();
          }
        } catch (err: any) {
          const status = err?.status ?? err?.response?.status;
          const body = err?.body ?? err?.data ?? err?.response?.data;
          if (status === 409 && body?.error === 'duplicate' && body?.existing && !force) {
            const ok = window.confirm(
              `Слово «${body.existing.original_text}» уже есть в общем словаре с переводом «${body.existing.main_translation_text}». Привязать ваше слово к существующему?`,
            );
            if (ok) return tryPromote(true);
            return;
          }
          throw err;
        }
      };
      await tryPromote(false);
    } catch (err) {
      captureError(err, { context: 'inline_word_edit_promote' });
      setError('Не удалось опубликовать в общий словарь');
    } finally {
      setPromoting(false);
    }
  };

  const handleAddAlt = () => {
    const trimmed = newAlt.trim();
    if (!trimmed) return;
    if (altTranslations.includes(trimmed)) {
      setNewAlt('');
      return;
    }
    setAltTranslations([...altTranslations, trimmed]);
    setNewAlt('');
  };

  const handleRemoveAlt = (index: number) => {
    setAltTranslations(altTranslations.filter((_, i) => i !== index));
  };

  const handleExampleChange = (index: number, field: keyof EditExample, value: string) => {
    const updated = [...examples];
    updated[index] = { ...updated[index], [field]: value };
    setExamples(updated);
  };

  const handleSaveExample = async (exampleId: number, index: number) => {
    try {
      const ex = examples[index];
      // Для UserWord — отдельный endpoint: WordExample имеет другую схему (нет blanked-полей).
      if (isUserWord) {
        await api.put(`/study/admin/user-examples/${exampleId}`, {
          sentence_en: ex.sentence_en,
          sentence_ru: ex.sentence_ru,
        });
      } else {
        await api.put(`/study/admin/examples/${exampleId}`, {
          sentence_en: ex.sentence_en,
          sentence_en_blanked: ex.sentence_en_blanked,
          sentence_ru: ex.sentence_ru,
          sentence_ru_blanked: ex.sentence_ru_blanked,
        });
      }
      // Optionally show a micro-toast here
    } catch (err) {
      captureError(err, { context: 'inline_example_save' });
      setError('Не удалось сохранить пример');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--theme-text-muted)' }}>
        Загрузка...
      </div>
    );
  }

  if (error && !wordData) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--theme-error)' }}>
        <div>{error}</div>
        <button onClick={loadWord} style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--theme-primary)', color: '#fff', cursor: 'pointer' }}>
          Повторить
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', maxWidth: 640, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0, fontSize: 20, color: 'var(--theme-text)' }}>
          Редактирование: <span style={{ fontWeight: 500 }}>{wordData?.original_text}</span>
        </h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: 'var(--theme-text-muted)' }}>
          ✕
        </button>
      </div>

      {isUserWord && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          padding: '10px 12px', marginBottom: 16,
          background: 'rgba(127,127,127,0.08)', border: '1px dashed var(--theme-border)', borderRadius: 8,
        }}>
          <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)' }}>
            👤 Пользовательское слово (только у вас в словаре)
          </div>
          <button
            onClick={handlePromote}
            disabled={promoting}
            type="button"
            style={{
              padding: '6px 12px', borderRadius: 6, border: 'none',
              background: 'var(--theme-primary)', color: '#fff', fontSize: 13,
              cursor: promoting ? 'not-allowed' : 'pointer', opacity: promoting ? 0.7 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            {promoting ? '...' : '📚 В общий словарь'}
          </button>
        </div>
      )}

      {saved && (
        <div style={{ padding: '12px 16px', background: 'rgba(0,230,118,0.12)', border: '1px solid var(--theme-success)', borderRadius: 10, marginBottom: 16, color: 'var(--theme-success)', fontWeight: 600 }}>
          ✅ Сохранено
          <button onClick={onClose} style={{ marginLeft: 12, padding: '4px 12px', borderRadius: 6, border: 'none', background: 'var(--theme-success)', color: '#fff', cursor: 'pointer', fontSize: 13 }}>
            Назад к тренировке
          </button>
        </div>
      )}

      {error && (
        <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid var(--theme-error)', borderRadius: 10, marginBottom: 16, color: 'var(--theme-error)', fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Main Translation */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 13, color: 'var(--theme-text-secondary)', marginBottom: 6 }}>Основной перевод</label>
        <input
          type="text"
          value={mainTranslation}
          onChange={(e) => setMainTranslation(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid var(--theme-border)',
            background: 'var(--theme-bg-elevated)',
            color: 'var(--theme-text)',
            fontSize: 15,
            outline: 'none',
          }}
        />
      </div>

      {/* Alternative Translations */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 13, color: 'var(--theme-text-secondary)', marginBottom: 6 }}>Альтернативные переводы</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {altTranslations.map((alt, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: 'var(--theme-bg-elevated)', borderRadius: 6, border: '1px solid var(--theme-border)', fontSize: 14, color: 'var(--theme-text)' }}>
              {alt}
              <button onClick={() => handleRemoveAlt(i)} style={{ background: 'none', border: 'none', color: 'var(--theme-error)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            value={newAlt}
            onChange={(e) => setNewAlt(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddAlt(); } }}
            placeholder="Добавить перевод..."
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid var(--theme-border)',
              background: 'var(--theme-bg-elevated)',
              color: 'var(--theme-text)',
              fontSize: 14,
              outline: 'none',
            }}
          />
          <button onClick={handleAddAlt} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--theme-primary)', color: '#fff', cursor: 'pointer', fontSize: 14 }}>
            +
          </button>
        </div>
      </div>

      {/* Examples — поддерживается и для UserWord (через user-examples endpoint) */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 13, color: 'var(--theme-text-secondary)', marginBottom: 6 }}>
          Примеры ({examples.length}){isUserWord && examples.length === 0 ? ' — у пользовательских слов часто нет примеров' : ''}
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {examples.map((ex, i) => (
            <div key={ex.id} style={{ padding: 12, background: 'var(--theme-bg-elevated)', borderRadius: 10, border: '1px solid var(--theme-border)' }}>
              <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginBottom: 6 }}>Пример #{i + 1}</div>
              <input
                type="text"
                value={ex.sentence_en}
                onChange={(e) => handleExampleChange(i, 'sentence_en', e.target.value)}
                placeholder="EN sentence"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 6,
                  border: '1px solid var(--theme-border)',
                  background: 'var(--theme-bg)',
                  color: 'var(--theme-text)',
                  fontSize: 14,
                  marginBottom: 6,
                  outline: 'none',
                }}
              />
              <input
                type="text"
                value={ex.sentence_ru}
                onChange={(e) => handleExampleChange(i, 'sentence_ru', e.target.value)}
                placeholder="RU translation"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 6,
                  border: '1px solid var(--theme-border)',
                  background: 'var(--theme-bg)',
                  color: 'var(--theme-text)',
                  fontSize: 14,
                  marginBottom: 8,
                  outline: 'none',
                }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => handleSaveExample(ex.id, i)}
                  style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: 'rgba(55,138,221,0.12)', color: '#378ADD', cursor: 'pointer', fontSize: 13 }}
                >
                  Сохранить пример
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Save Word Button */}
      <div style={{ display: 'flex', gap: 10, paddingTop: 8, borderTop: '1px solid var(--theme-border)' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            flex: 1,
            padding: '12px',
            borderRadius: 10,
            border: 'none',
            background: 'var(--theme-success)',
            color: '#fff',
            fontSize: 15,
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Сохранение...' : '💾 Сохранить слово'}
        </button>
        <button
          onClick={onClose}
          style={{
            padding: '12px 20px',
            borderRadius: 10,
            border: '1px solid var(--theme-border)',
            background: 'var(--theme-bg-elevated)',
            color: 'var(--theme-text)',
            fontSize: 15,
            cursor: 'pointer',
          }}
        >
          Отмена
        </button>
      </div>
    </div>
  );
}
